import type {
    CentraSelectionItem,
    ICentraCheckoutSelection,
    ICentraOrder,
    ICentraSelectionProduct,
} from '../../interfaces';
import { getBaseUrl } from '../utils';
import {
    getItemName,
    selectionItemProductToKlaviyoItem,
    selectionItemToKlaviyoItem,
} from './utils';

type KlaviyoEventProperties = Record<string, unknown>;

type QueuedEvent = {
    eventName: string;
    properties: KlaviyoEventProperties;
};

export const klaviyoTracker = {
    _isIdentified: false,
    _eventQueue: [] as QueuedEvent[],
    _identityData: null as { email?: string; firstName?: string; lastName?: string } | null,
    _checkInterval: null as NodeJS.Timeout | null,

    _startKlaviyoCheck: () => {
        if (
            klaviyoTracker._checkInterval ||
            (klaviyoTracker._eventQueue.length === 0 && !klaviyoTracker._identityData)
        ) {
            return;
        }

        klaviyoTracker._checkInterval = setInterval(() => {
            if (window.klaviyo) {
                klaviyoTracker.tryFlush();
                if (
                    klaviyoTracker._checkInterval &&
                    klaviyoTracker._eventQueue.length === 0
                ) {
                    clearInterval(klaviyoTracker._checkInterval);
                    klaviyoTracker._checkInterval = null;
                }
            }
        }, 500);
    },

    _flushQueue: () => {
        if (!klaviyoTracker._isIdentified || !window.klaviyo) return;

        // Identify user first if we have identity data
        if (klaviyoTracker._identityData) {
            window.klaviyo.identify({
                $email: klaviyoTracker._identityData.email,
                $first_name: klaviyoTracker._identityData.firstName,
                $last_name: klaviyoTracker._identityData.lastName,
            });
        }

        while (klaviyoTracker._eventQueue.length > 0) {
            const event = klaviyoTracker._eventQueue.shift();
            if (event) {
                window.klaviyo.track(event.eventName, event.properties);
            }
        }
    },

    tryFlush: () => {
        if (window.klaviyo && klaviyoTracker._isIdentified) {
            klaviyoTracker._flushQueue();
        }
    },

    _trackEvent: (eventName: string, properties: KlaviyoEventProperties) => {
        if (klaviyoTracker._isIdentified && window.klaviyo) {
            window.klaviyo.track(eventName, properties);
        } else {
            klaviyoTracker._eventQueue.push({ eventName, properties });
            klaviyoTracker._startKlaviyoCheck();
        }
    },

    addToCart: ({
        language,
        selection,
        item,
    }: {
        language: string;
        selection?: ICentraCheckoutSelection;
        item?: CentraSelectionItem;
    }) => {
        if (!selection || !selection.items || selection.items.length === 0)
            return;

        const addedItem = (item || selection.items.at(selection.items.length - 1)) as
            | CentraSelectionItem
            | undefined;

        if (!addedItem) return;

        const baseUrl = getBaseUrl();

        const ecommerce = {
            $value: selection?.totals?.grandTotalPriceAsNumber,
            AddedItemProductName: addedItem.product?.name,
            AddedItemProductID: addedItem.product?.productSku,
            AddedItemSKU: addedItem.product?.productSku,
            AddedItemCategories:
                'categoryName' in addedItem.product ? addedItem.product?.categoryName : [],
            AddedItemImageURL: addedItem.product?.media?.['standard']?.[0],
            AddedItemURL: `${baseUrl}/en/shop/${addedItem.product?.uri}`,
            AddedItemPrice: addedItem.priceEach,
            AddedItemQuantity: 1,
            ItemNames: selection.items.map(item => item.product?.name),
            CheckoutURL: `${baseUrl}/checkout`,
            Items: selection?.items?.map(item => selectionItemToKlaviyoItem(item, language)),
        };

        klaviyoTracker._trackEvent('Added to Cart', ecommerce);
    },
    removeFromCart: ({
        language,
        selection,
        item,
    }: {
        language: string;
        selection?: ICentraCheckoutSelection;
        item?: CentraSelectionItem;
    }) => {
        if (!selection || !selection.items || selection.items.length === 0)
            return;

        const removedItem = item;

        if (!removedItem) return;

        const baseUrl = getBaseUrl();

        const ecommerce = {
            $value: selection?.totals?.grandTotalPriceAsNumber,
            RemovedItemProductName: removedItem.product?.name,
            RemovedItemProductID: removedItem.product?.productSku,
            RemovedItemSKU: removedItem.product?.productSku,
            RemovedItemCategories:
                'categoryName' in removedItem.product ? removedItem.product?.categoryName : [],
            RemovedItemImageURL: removedItem.product?.media?.['standard']?.[0],
            RemovedItemURL: `${baseUrl}/en/shop/${removedItem.product?.uri}`,
            RemovedItemPrice: removedItem.priceEach,
            RemovedItemQuantity: 1,
            ItemNames: selection.items.map(item => item.product?.name),
            CheckoutURL: `${baseUrl}/checkout`,
            Items: selection?.items?.map(item => selectionItemToKlaviyoItem(item, language)),
        };

        klaviyoTracker._trackEvent('Removed from Cart', ecommerce);
    },
    viewProduct: ({ item, language }: { item: ICentraSelectionProduct; language: string }) => {
        const ecommerce = selectionItemProductToKlaviyoItem(item, language);

        klaviyoTracker._trackEvent('Viewed Product', ecommerce);
    },
    startCheckout: ({
        selection,
      language,
        token
    }: {
        selection: ICentraCheckoutSelection;
        language: string;
        token: string
      }) => {

        if (!selection || !selection.items || selection.items.length === 0)
          return;



        const ecommerce = {
            $event_id: `started-checkout-${Math.floor(Date.now() / 1000)}`,
            $value: selection?.totals.grandTotalPriceAsNumber,
            $currency: selection?.currency,
            ItemNames: selection?.items.map(item => getItemName(item, language)),
            CheckoutUrl: `${window.location.origin}/${language}/checkout?token=${token}`,
            Categories: selection?.items.map(item => item.product.collectionName),
            Items: selection?.items?.map(item => selectionItemToKlaviyoItem(item, language)),
        };

        klaviyoTracker._trackEvent('Started Checkout', ecommerce);
    },
    placedOrder: ({ order }: { order: ICentraOrder }) => {
        const ecommerce = {
            $event_id: `placed-order-${Math.floor(Date.now() / 1000)}`,
            $value: order?.totals.grandTotalPriceAsNumber,
            CustomerCurrency: order?.currency,
            GrossPaidPriceInCustomerCurrency: order?.totals.grandTotalPriceAsNumber,
            TaxAmount: order?.totals.taxPercent,
            Categories: [...new Set(order?.items.map(item => item.product.collectionName))],
            ItemNames: order?.items.map(item => item.product.name),
            Brands: ['Northern Playground'],
            DiscountValue: order?.totals.totalDiscountPriceAsNumber,
            Items: order?.items.map(item => ({
                ProductID: item.product.centraProduct,
                SKU: item.product.sku,
                ProductName: item.product.name,
                Quantity: item.quantity,
                EAN: item.ean,
                Size: item.size,
                VariantName: item.product.color_swatch?.name ?? '',
                GrossPaidPrice: item.totalPriceAsNumber,
                GrossPaidPriceInCustomerCurrency: item.totalPriceAsNumber,
                OriginalPrice: item.totalPriceBeforeDiscountAsNumber,
                OriginalPriceInCustomerCurrency: item.totalPriceBeforeDiscountAsNumber,
                GrossPaidPricePerUnit: item.priceEachAsNumber,
                GrossPaidPricePerUnitInCustomerCurrency: item.priceEachAsNumber,
                ImageURL: item.product?.media?.standard?.[0],
                Categories: [item.product.collectionName],
                Brand: 'Northern Playground',
            })),
            BillingAddress: {
                FirstName: order?.address?.firstName,
                LastName: order?.address?.lastName,
                Address1: order?.address?.address1,
                Address2: order?.address?.address2,
                City: order?.address?.city,
                Region: order?.address?.state,
                Country: order?.address?.country,
                Zip: order?.address?.zipCode,
                Phone: order?.address?.phoneNumber,
            },
            ShippingAddress: {
                FirstName: order?.shippingAddress?.firstName,
                LastName: order?.shippingAddress?.lastName,
                Address1: order?.shippingAddress?.address1,
                Address2: order?.shippingAddress?.address2,
                City: order?.shippingAddress?.city,
                Region: order?.shippingAddress?.state,
                Country: order?.shippingAddress?.country,
                Zip: order?.shippingAddress?.zipCode,
                Phone: order?.shippingAddress?.phoneNumber,
            },
            time: Math.floor(Date.now() / 1000),
        };

        klaviyoTracker._trackEvent('Placed Order', ecommerce);
    },
    searchedSite: ({ searchTerm, resultsCount }: { searchTerm: string; resultsCount: number }) => {
        if (!searchTerm) return;

        const ecommerce = {
            SearchTerm: searchTerm,
            ResultsCount: resultsCount,
        };

        klaviyoTracker._trackEvent('Searched Site', ecommerce);
    },
    viewedCategory: ({
        categoryId,
        categoryName,
        url,
    }: {
        categoryName: string;
        categoryId: string;
        url: string;
    }) => {
        const ecommerce = {
            CategoryName: categoryName,
            ...(categoryId && { CategoryID: categoryId }),
            URL: url,
        };

        klaviyoTracker._trackEvent('Viewed Category', ecommerce);
    },
    identify: ({
        email,
        firstName,
        lastName,
    }: {
        email?: string;
        firstName?: string;
        lastName?: string;
    }) => {
        if (!email?.trim() && !firstName?.trim() && !lastName?.trim()) return;

        klaviyoTracker._identityData = { email, firstName, lastName };
        klaviyoTracker._isIdentified = true;

        if (window.klaviyo) {
            window.klaviyo.identify({
                $email: email,
                $first_name: firstName,
                $last_name: lastName,
            });
            klaviyoTracker._flushQueue();
        } else {
            klaviyoTracker._startKlaviyoCheck();
        }
    },
};
